import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import { Label } from "components/label";
import { Tag } from "components/tag";
import Textarea from "components/textarea";
import {
  DiscountType,
  ICreateCouponForm,
  IPromotionConditionDetail,
  PaymentMethodKey,
} from "interfaces";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { getPaymentMethodLabel } from "utills/helpers";
interface AdditionalSetupProps {
  conditionDetailData: IPromotionConditionDetail;
}
export const AdditionalSetup = ({
  conditionDetailData,
}: AdditionalSetupProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ICreateCouponForm>(); // retrieve all hook methods

  const [isShowCouponDisplay, setIsShowCouponDisplay] = useState(false);

  const shouldShowCouponDisplay = () => {
    const discountTypeForCouponDisplay = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForCouponDisplay.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowPaymentMethods = () => {
    const discountTypeForPaymentMethods = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
      DiscountType.QUANTITY_DISCOUNT,
      DiscountType.CAMPAIGN_DISCOUNT,
    ];
    return discountTypeForPaymentMethods.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowUsagePerAccount = () => {
    const discountTypeForUsagePerAccount = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForUsagePerAccount.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowDailyActiveTime = () => {
    const discountTypeForDailyActiveTime = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForDailyActiveTime.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const handleCouponDisplayToggle = (checked: boolean) => {
    setIsShowCouponDisplay(checked);
    setValue("coupons.displayed_on_actiup", false);
    setValue("coupons.displayed_on_whitelabel", false);
    setValue("coupons.short_desc_en", "");
    setValue("coupons.short_desc_vi", "");
  };

  return (
    <CollapsibleSection label="Additional Set-up" defaultOpen={false}>
      <div className="box-container">
        <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
          {shouldShowCouponDisplay() && (
            <>
              <div className="col-span-full">
                <Input
                  type="checkbox"
                  label="Coupon Display"
                  id="couponDisplay"
                  checked={isShowCouponDisplay}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCouponDisplayToggle(e.target.checked)
                  }
                />
              </div>
              {isShowCouponDisplay && (
                <>
                  <div className="col-span-full flex items-center gap-x-4">
                    <Controller
                      control={control}
                      name="coupons.displayed_on_actiup"
                      render={({ field: { onChange, value = true, ref } }) => (
                        <Input
                          type="checkbox"
                          label="Display on ActiUp"
                          id="coupons.displayed_on_actiup"
                          checked={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(e.target.checked)
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="coupons.displayed_on_whitelabel"
                      render={({ field: { onChange, value = true, ref } }) => (
                        <Input
                          type="checkbox"
                          label="Display on Whitelabel"
                          id="coupons.displayed_on_whitelabel"
                          checked={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(e.target.checked)
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-full">
                    <Label
                      id="coupons.short_desc_vi"
                      name="Coupon Terms of Use"
                    />
                    <div>
                      <Controller
                        control={control}
                        name="coupons.short_desc_vi"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <div className="relative">
                              <Textarea
                                id="coupons.short_desc_vi"
                                placeholder="Coupon Terms of Use (vi)"
                                className="pr-10"
                                error={errors["coupons"]?.short_desc_vi}
                                onChange={onChange}
                                value={value}
                              />
                              <img
                                src="/images/flags/circle-vi.png"
                                alt="Language Icon"
                                className="size-5 absolute top-3 right-3"
                              />
                            </div>
                          );
                        }}
                      />
                      {errors["coupons"]?.short_desc_vi ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["coupons"]?.short_desc_vi?.message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <Controller
                        control={control}
                        name="coupons.short_desc_en"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <div className="relative">
                              <Textarea
                                id="coupons.short_desc_en"
                                placeholder="Coupon Terms of Use (en)"
                                className="pr-10"
                                error={errors["coupons"]?.short_desc_en}
                                onChange={onChange}
                                value={value}
                              />
                              <img
                                src="/images/flags/circle-en.png"
                                alt="Language Icon"
                                className="size-5 absolute top-3 right-3"
                              />
                            </div>
                          );
                        }}
                      />
                      {errors["coupons"]?.short_desc_en ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["coupons"]?.short_desc_en?.message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {shouldShowPaymentMethods() && (
            <div className="col-span-full sm:col-span-4">
              <Input
                type="checkbox"
                label="Payment Methods"
                id="isShowPaymentMethod"
                checked={conditionDetailData?.apply_to_payment_methods}
                disabled
              />
              {conditionDetailData?.apply_to_payment_methods && (
                <div className="mt-4">
                  <div className="flex items-center flex-wrap gap-2.5">
                    {(conditionDetailData?.apply_to_payment_methods ?? []).map(
                      (method) => (
                        <Tag
                          text={getPaymentMethodLabel(method)}
                          key={method}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {shouldShowUsagePerAccount() && (
            <div className="col-span-full sm:col-span-4">
              <Input
                type="checkbox"
                label="Max Usage Per Account"
                id="isShowUsagePerAccount"
                checked={conditionDetailData?.max_times_used_per_account}
                disabled
              />
              {conditionDetailData?.max_times_used_per_account && (
                <div className="mt-4">
                  <p>{conditionDetailData?.max_times_used_per_account}</p>
                </div>
              )}
            </div>
          )}
          {shouldShowDailyActiveTime() && (
            <div className="col-span-full">
              <Input
                type="checkbox"
                label="Daily Active Time"
                id="isShowDailyActiveTime"
                checked={
                  conditionDetailData?.apply_from_hour ||
                  conditionDetailData?.apply_to_hour ||
                  conditionDetailData?.max_times_used_per_day
                }
                disabled
              />
              {(conditionDetailData?.apply_from_hour ||
                conditionDetailData?.apply_to_hour ||
                conditionDetailData?.max_times_used_per_day) && (
                <div className="w-full mt-4 flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label
                      required
                      id="apply_from_hour"
                      name="Daily Start Time"
                    />
                    <p>{conditionDetailData?.apply_from_hour}</p>
                  </div>
                  <div className="flex-1">
                    <Label required id="apply_to_hour" name="Daily End Time" />
                    <p>{conditionDetailData?.apply_to_hour}</p>
                  </div>
                  <div className="flex-1">
                    <Label
                      id="max_times_used_per_day"
                      name="Daily Usage Limit"
                    />
                    <p>{conditionDetailData?.max_times_used_per_day}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
};
